import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
} from 'react-router-dom';
import ReportPage from './pages/ReportPage';

/**
 * The route element, keyed on the token it is showing.
 *
 * `WeekNavigator` moves between weeks client-side, which swaps the route
 * param without remounting — so the page kept the previous week's report,
 * kept `loading` false so nothing said a fetch was running, kept a `notFound`
 * from an earlier week for ever (it is only ever set true), and kept
 * `ScanGrid`'s selected keyword INDEX, which throws on a week with fewer
 * keywords.
 *
 * Keyed rather than reset field by field: a report is identified by its
 * token, and every one of those is the same mistake — reusing a view of one
 * report for another. The key also covers state in children, including state
 * added later that nobody remembers to reset here.
 */
export function ReportRoute({ preview = false }: { preview?: boolean }) {
  const { shareToken, previewToken } = useParams<{
    shareToken: string;
    previewToken: string;
  }>();
  const token = (preview ? previewToken : shareToken) ?? 'missing';
  return <ReportPage key={token} preview={preview} />;
}

/**
 * The hosted weekly report.
 *
 * One route, and deliberately no shell: no navigation, no account menu, no
 * sign-in. The reader is the agency's client, who has no account here and
 * arrived from a link in an email — anything that looks like an app to log into
 * is a wrong turn for them.
 */
function ReportsApp() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path='/report/:shareToken' element={<ReportRoute />} />
        {/* A preview the agency opened from the app: the same page, never
            sent, gone within the hour. */}
        <Route
          path='/report/preview/:previewToken'
          element={<ReportRoute preview />}
        />
        {/* No index page: without a token there is no report to show, and a
            landing page here would invite people to look for one. */}
        <Route path='*' element={<Navigate to='/report/missing' replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default ReportsApp;
